import type React from 'react'
import type { TextareaHTMLAttributes } from 'react'
import { useRef } from 'react'
import { useDictation } from '../hooks/useDictation'
import { cn } from '../lib/cva'
import { DictationControls } from './dictation/DictationControls'
import type { DictationReviewSnapshot, DictationSessionToken, DictationTone } from './dictation/dictation.types'
import {
	type BaseFormElementProps,
	type FormElementStyleProps,
	FormElementWrapper,
	formStyles,
	useFormElementState,
} from './FormElementWrapper'

interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'disabled'>, BaseFormElementProps {
	rows?: number
	dictationFieldId?: string
	dictationEnabled?: boolean
	allowAiRewrite?: boolean
	onRequestDictationToken?: () => Promise<DictationSessionToken | null>
	onRewriteDictatedText?: (input: string, tone: DictationTone) => Promise<string>
	onDictationSnapshot?: (snapshot: DictationReviewSnapshot) => void
}

export const TextArea: React.FC<TextAreaProps> = ({
	label,
	hint,
	showHintIcon = false,
	variant = 'default',
	disabled = false,
	className,
	value: controlledValue,
	defaultValue,
	onChange,
	isRequired = false,
	rows = 4,
	dictationFieldId,
	dictationEnabled = false,
	allowAiRewrite = false,
	onRequestDictationToken,
	onRewriteDictatedText,
	onDictationSnapshot,
	...props
}) => {
	const { value, handleChange, handleFocus, handleBlur, styleProps } = useFormElementState<HTMLTextAreaElement>({
		value: controlledValue,
		defaultValue,
		variant,
		disabled,
		onChange,
	})
	const generatedFieldIdRef = useRef(`textarea-${crypto.randomUUID()}`)
	const fieldId = dictationFieldId || (typeof props.id === 'string' ? props.id : generatedFieldIdRef.current)
	const dictation = useDictation({
		fieldId,
		value: String(value ?? ''),
		profile: 'bullets',
		onRequestSessionToken: onRequestDictationToken,
		onSnapshot: onDictationSnapshot,
		onRewrite: onRewriteDictatedText,
		onChangeValue: (next) => {
			const syntheticEvent = {
				target: { value: next },
			} as React.ChangeEvent<HTMLTextAreaElement>
			handleChange(syntheticEvent)
		},
	})

	const renderTextArea = (textAreaStyleProps?: FormElementStyleProps) => {
		const mergedStyleProps = textAreaStyleProps || styleProps

		return (
			<div className='w-full relative'>
				<textarea
					className={cn(
						formStyles.element(mergedStyleProps),
						'resize-none -mb-1.5',
						mergedStyleProps.isFocused && '-mb-[7px]',
						className,
					)}
					disabled={disabled}
					value={value}
					onChange={handleChange}
					onFocus={handleFocus}
					onBlur={handleBlur}
					rows={rows}
					{...props}
				/>
				{dictationEnabled && (
					<div className='mt-1'>
						<DictationControls
							allowRewrite={allowAiRewrite}
							isListening={dictation.isListening}
							isProcessing={dictation.state === 'processing'}
							error={dictation.error}
							interimTranscript={dictation.interimTranscript}
							draftTranscript={dictation.draftTranscript}
							onStart={() => void dictation.startDictation()}
							onStop={dictation.stopDictation}
							onInsert={dictation.insertDraft}
							onReplace={dictation.replaceWithDraft}
							canUndoRewrite={dictation.canUndoRewrite}
							onDiscard={dictation.discardDraft}
							onReset={dictation.resetDraft}
							onRewrite={(tone) => void dictation.rewriteText(tone)}
							onUndoRewrite={dictation.undoRewrite}
						/>
					</div>
				)}
			</div>
		)
	}

	return (
		<FormElementWrapper
			label={label}
			hint={hint}
			showHintIcon={showHintIcon}
			variant={variant}
			disabled={disabled}
			isRequired={isRequired}
			styleProps={styleProps}>
			{renderTextArea()}
		</FormElementWrapper>
	)
}
